import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Salad, Search, Plus, RefreshCw, Loader2, Trash2, Sparkles, Check, X, ScanBarcode, ExternalLink, Database, Pencil, Copy, UserPlus, AlertTriangle, Camera, ImageOff, Clock, ChefHat, Users, Inbox, CalendarClock, ClipboardX, Hourglass, CircleAlert, ShoppingBag, Coins, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { NativeSelect } from '@/components/ui/native-select';
import { ernaehrungAPI, checkinsAPI, bildSrc } from '@/lib/api';
import RezeptAnsicht from '@/components/RezeptAnsicht';
import { Preis, euro, preisEingabe, preisZahl } from '@/components/ErnaehrungPreis';
import { FunktionenSchalter } from '@/components/GenussKarte';
import { AboProduktEinstellung, EinwilligungFreigabe } from '@/components/ErnaehrungAbo';
import { KursTipps } from '@/components/KursTipps';
import { ErnaehrungTresor } from '@/components/ErnaehrungTresor';
import { KommentarVorlagen, RisikoEinstellung, WearableSchwellen } from '@/components/ErnaehrungCockpitEinstellungen';
import { MedienFeedbackEinstellung } from '@/components/MedienFeedbackAufnahme';

/* Ernährung (10.10.2026): Übersicht aller Mitglieder mit Angaben oder Plan — rote Ampel zuerst — und die
   P1-Rezepte. Die Pläne selbst sieht das Mitglied in der Kunden-App; hier legen Trainer sie an. */

export const AMPEL = {
  rot: { label: 'braucht Kontakt', cls: 'bg-red-500/10 text-red-700 border-red-200', punkt: 'bg-red-500' },
  gelb: { label: 'im Blick behalten', cls: 'bg-amber-500/10 text-amber-700 border-amber-200', punkt: 'bg-amber-500' },
  gruen: { label: 'läuft', cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-200', punkt: 'bg-emerald-500' },
};
export const MAHLZEIT = { fruehstueck: 'Frühstück', mittag: 'Mittagessen', snack: 'Snack', abend: 'Abendessen' };   // Tagesreihenfolge
const MERKMALE = ['laktose', 'gluten', 'nuss', 'erdnuss', 'fisch', 'fleisch', 'schwein', 'tierisch'];

export function lokal(iso) { const [y, m, d] = String(iso || '').slice(0, 10).split('-').map(Number); return new Date(y, m - 1, d); }
export function datum(iso, opt = { day: '2-digit', month: '2-digit' }) { return iso ? lokal(iso).toLocaleDateString('de-DE', opt) : ''; }
export function fehlertext(e, rest = 'Das hat nicht geklappt') {
  if (e?.response?.status === 403) return 'Dafür fehlt dir das Recht (Ernährung).';
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : rest;
}

export function MitgliedSuche({ onWahl }) {
  const [q, setQ] = useState('');
  const [treffer, setTreffer] = useState([]);
  const [sucht, setSucht] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setTreffer([]); return undefined; }
    const t = setTimeout(async () => {
      setSucht(true);
      try { setTreffer((await checkinsAPI.suche(q.trim(), 8)).data.treffer || []); }
      catch { setTreffer([]); }
      finally { setSucht(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative w-full sm:w-80">
      <label htmlFor="ernaehrung-suche" className="sr-only">Mitglied suchen</label>
      <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" aria-hidden="true" />
      <Input id="ernaehrung-suche" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mitglied suchen und starten …" className="pl-8" autoComplete="off" />
      {q.trim().length >= 2 && (
        <ul className="absolute z-20 mt-1 w-full rounded-md border bg-popover shadow-md max-h-72 overflow-auto" role="listbox">
          {sucht && <li className="px-3 py-2 text-sm text-muted-foreground">Sucht …</li>}
          {!sucht && treffer.length === 0 && <li className="px-3 py-2 text-sm text-muted-foreground">Niemand gefunden</li>}
          {treffer.map((t) => (
            <li key={t.id}>
              <button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-accent focus:bg-accent focus:outline-none truncate" onClick={() => onWahl(t)}>
                {t.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const KACHELN = [
  ['laufend', 'Coachings laufen', Users], ['buchungen_offen', 'Buchungen warten', ShoppingBag], ['feedback_offen', 'Feedback offen', Inbox],
  ['checkins_ueberfaellig', 'Check-ins überfällig', ClipboardX], ['plaene_faellig', 'Pläne fällig', CalendarClock],
  ['coachings_enden', 'Coachings enden bald', Hourglass], ['ampel_rot', 'Braucht Kontakt', CircleAlert],
  // Kommentare (Stufe 2): Die Kachel liefert der Server nur mit dem Schalter „Kommentare“ — sonst fehlt sie hier
  ['antworten_offen', 'Antworten ungelesen', MessageCircle],
];
const GRUPPEN = [['ueberfaellig', 'Überfällig'], ['heute', 'Heute'], ['woche', 'Diese Woche'], ['spaeter', 'Später']];

/* Coach-Dashboard (Paket 5): Kacheln und To-dos, aus den Daten gerechnet — Erledigtes verschwindet von selbst. */
/* Anzeige allein (auch auf der Trainer-Webseite /coach, dort mit eigener Anmeldung). */
export function DashboardAnsicht({ d, onOeffne }) {
  const [gruppe, setGruppe] = useState(d.gruppen.ueberfaellig ? 'ueberfaellig' : 'heute');
  const liste = d.todos.filter((t) => t.gruppe === gruppe);
  return (
    <section aria-label="Coach-Dashboard" className="space-y-3">
      <ul className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {KACHELN.filter(([k]) => k in d.kacheln).map(([k, label, Icon]) => (
          <li key={k} className={`rounded-lg border bg-background p-3 ${d.kacheln[k] && k !== 'laufend' ? 'border-primary/40 bg-primary/5' : ''}`}>
            <Icon className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <p className="text-2xl font-semibold tabular-nums mt-1">{d.kacheln[k]}</p>
            <p className="text-xs text-muted-foreground leading-tight">{label}</p>
          </li>
        ))}
      </ul>
      <Card>
        <CardContent className="p-0">
          <div role="tablist" aria-label="To-dos" className="grid grid-cols-4 border-b">
            {GRUPPEN.map(([k, label]) => (
              <button key={k} type="button" role="tab" aria-selected={gruppe === k} onClick={() => setGruppe(k)}
                className={`py-2 text-center border-b-2 -mb-px focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${gruppe === k ? 'border-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                <span className={`block text-lg font-semibold tabular-nums ${k === 'ueberfaellig' && d.gruppen[k] ? 'text-red-700' : ''}`}>{d.gruppen[k]}</span>
                <span className="block text-xs">{label}</span>
              </button>
            ))}
          </div>
          {liste.length === 0 ? <p className="p-4 text-sm text-muted-foreground text-center">Nichts zu tun{gruppe === 'ueberfaellig' ? ' — alles im Plan' : ''}.</p> : (
            <ul className="divide-y">
              {liste.map((t) => (
                <li key={`${t.art}-${t.lead_id}-${t.faellig}-${t.text}`}>
                  <button type="button" onClick={() => onOeffne(t)} className="w-full text-left px-4 py-2.5 hover:bg-accent/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring flex flex-col sm:flex-row sm:items-center gap-x-3">
                    <span className="font-medium shrink-0">{t.name || 'Mitglied'}</span>
                    <span className="text-sm text-muted-foreground flex-1 min-w-0">{t.text}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{datum(t.faellig)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

/* Coach-Dashboard (Paket 5): Kacheln und To-dos, aus den Daten gerechnet — Erledigtes verschwindet von selbst. */
function Dashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { ernaehrungAPI.dashboard().then((r) => setD(r.data)).catch(() => setD(false)); }, []);
  if (d === false) return null;
  if (!d) return <Skeleton className="h-40 w-full" />;
  return <DashboardAnsicht d={d} onOeffne={(t) => navigate(t.link)} />;
}

function Mitglieder() {
  const navigate = useNavigate();
  const [zeilen, setZeilen] = useState(null);
  const [fehler, setFehler] = useState('');
  const laden = useCallback(async () => {
    setFehler('');
    try { setZeilen((await ernaehrungAPI.uebersicht()).data.mitglieder); }
    catch (e) { setFehler(fehlertext(e, 'Übersicht konnte nicht geladen werden')); setZeilen([]); }
  }, []);
  useEffect(() => { laden(); }, [laden]);

  return (
    <div className="space-y-3">
      <Dashboard />
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
        <MitgliedSuche onWahl={(t) => navigate(`/ernaehrung/${t.id}`)} />
        <div className="flex gap-2">
          {/* E23 (15.10.2026): „Neuer Kunde in einem Fluss“ — Details · Vorlagen · Mitgliedschaft, bestätigt nur die Leitung */}
          <Button size="sm" onClick={() => navigate('/ernaehrung/abschluss')} data-testid="ernaehrung-neuer-kunde"><Plus className="w-4 h-4 mr-1" />Neuer Kunde</Button>
          <Button variant="outline" size="sm" onClick={laden}><RefreshCw className="w-4 h-4 mr-1" />Aktualisieren</Button>
        </div>
      </div>
      {fehler && <p className="text-sm text-destructive" role="alert">{fehler}</p>}
      {zeilen === null ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
      ) : zeilen.length === 0 && !fehler ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          Noch niemand mit Ernährungsplan. Such oben ein Mitglied, trag die Angaben ein und erzeuge den ersten Wochenplan.
        </CardContent></Card>
      ) : (
        <ul className="divide-y rounded-md border">
          {zeilen.map((z) => (
            <li key={z.lead_id}>
              <Link to={`/ernaehrung/${z.lead_id}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-accent/40 focus:bg-accent/40 focus:outline-none">
                <span className={`w-2.5 h-2.5 shrink-0 rounded-full ${AMPEL[z.ampel].punkt}`} aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{z.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {z.plan ? `Plan ${datum(z.plan.start)}–${datum(z.plan.ende)}` : 'kein laufender Plan'}
                    {' · '}{z.trainings_14_tage} {z.trainings_14_tage === 1 ? 'Training' : 'Trainings'} / 14 Tage
                    {z.veraenderung_kg !== null && z.veraenderung_kg !== undefined ? ` · ${z.veraenderung_kg > 0 ? '+' : ''}${String(z.veraenderung_kg).replace('.', ',')} kg` : ''}
                  </p>
                </div>
                <Badge variant="outline" className={`shrink-0 ${AMPEL[z.ampel].cls}`} title={z.gruende.join(' · ') || 'alles im grünen Bereich'}>
                  {AMPEL[z.ampel].label}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const LEER_ZUTAT = { lebensmittel_id: '', gramm: '', einheit: '', gesperrt: false, alternativen: [] };
const LEER_REZEPT = { name: '', mahlzeiten: [], minuten: '', zubereitung: '', gewuerze: '', schwierigkeit: 'einfach', budget: 'preiswert', zutaten: [LEER_ZUTAT],
  zubereitungszeit_min: '', kochzeit_min: '', portionen: '1', tags: '' };
// E3: ein vorhandenes Rezept in das Formular — Haushaltsmaß-Zutaten zeigen ihre Menge in der Einheit, sonst Gramm
const ausRezept = (v) => ({
  name: v.name || '', mahlzeiten: v.mahlzeiten || [], minuten: v.minuten ?? '', zubereitung: v.zubereitung || '', gewuerze: (v.gewuerze || []).join(', '),
  schwierigkeit: v.schwierigkeit || 'einfach', budget: v.budget || 'preiswert', zubereitungszeit_min: v.zubereitungszeit_min ?? '', kochzeit_min: v.kochzeit_min ?? '',
  portionen: String(v.portionen || 1), tags: (v.tags || []).join(', '),
  zutaten: (v.zutaten || []).map((z) => ({ lebensmittel_id: z.lebensmittel_id, gramm: z.einheit ? String(z.menge ?? '') : String(z.gramm ?? ''), einheit: z.einheit || '', gesperrt: !!z.gesperrt, alternativen: z.alternativen || [] })),
});

function RezeptDialog({ offen, onClose, lebensmittel, onGespeichert, vorlage = null }) {
  const [r, setR] = useState(LEER_REZEPT);
  const [speichert, setSpeichert] = useState(false);
  useEffect(() => { if (offen) setR(vorlage ? ausRezept(vorlage) : LEER_REZEPT); }, [offen, vorlage]);
  const optionen = useMemo(() => lebensmittel.filter((l) => l.aktiv !== false).map((l) => ({ value: l.id, label: l.quelle === 'openfoodfacts' ? `${l.name} (Open Food Facts)` : l.name })), [lebensmittel]);
  const je = useMemo(() => Object.fromEntries(lebensmittel.map((l) => [l.id, l])), [lebensmittel]);
  const zutat = (i, feld, wert) => setR((x) => ({ ...x, zutaten: x.zutaten.map((z, j) => (j === i ? { ...z, [feld]: wert } : z)) }));
  // Gramm je Zeile: direkt, oder Menge × Grammwert des Haushaltsmaßes (E0) — der Server rechnet beim Speichern genauso
  const grammVon = (z) => {
    const l = je[z.lebensmittel_id];
    const m = Number(String(z.gramm).replace(',', '.')) || 0;
    if (!z.einheit) return m;
    const e = (l?.einheiten || []).find((x) => x.name === z.einheit);
    return e ? m * e.g : 0;
  };
  // Nährwerte live beim Eintippen (Natty-Gains-Muster): je 100 g aus der Studio-Liste
  const live = useMemo(() => {
    const n = { kcal: 0, eiweiss: 0, fett: 0, kh: 0 };
    r.zutaten.forEach((z) => { const l = je[z.lebensmittel_id]; const g = grammVon(z); if (l) Object.keys(n).forEach((k) => { n[k] += ((l[k] || 0) * g) / 100; }); });
    const energie = n.eiweiss * 4 + n.kh * 4 + n.fett * 9;
    return energie ? { ...n, anteile: { eiweiss: Math.round((n.eiweiss * 4 / energie) * 100), kh: Math.round((n.kh * 4 / energie) * 100), fett: Math.round((n.fett * 9 / energie) * 100) } } : null;
  }, [r.zutaten, je]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Richtpreis live wie die Nährwerte: Gramm × Preis je kg; nach dem Speichern rechnet der Server
  const livePreis = useMemo(() => {
    let summe = 0;
    const ohne = [];
    const gewaehlt = r.zutaten.filter((z) => je[z.lebensmittel_id] && grammVon(z) > 0);
    gewaehlt.forEach((z) => {
      const l = je[z.lebensmittel_id];
      if (l.preis_kg) summe += (grammVon(z) * l.preis_kg) / 1000;
      else if (!ohne.includes(l.name)) ohne.push(l.name);
    });
    return gewaehlt.length ? { euro: Math.round(summe * 100) / 100, vollstaendig: !ohne.length, ohne } : null;
  }, [r.zutaten, je]);   // eslint-disable-line react-hooks/exhaustive-deps
  const zahlOderNull = (v) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(',', '.')));
  const speichern = async (e) => {
    e.preventDefault();
    const zutaten = r.zutaten.filter((z) => z.lebensmittel_id && Number(String(z.gramm).replace(',', '.')) > 0).map((z) => ({
      lebensmittel_id: z.lebensmittel_id,
      ...(z.einheit ? { menge: Number(String(z.gramm).replace(',', '.')), einheit: z.einheit } : { gramm: Number(String(z.gramm).replace(',', '.')) }),
      gesperrt: !!z.gesperrt, alternativen: (z.alternativen || []).filter((a) => a !== z.lebensmittel_id).slice(0, 5),
    }));
    if (!r.name.trim() || !r.mahlzeiten.length || !zutaten.length) { toast.error('Name, mindestens eine Mahlzeit und eine Zutat mit Menge'); return; }
    setSpeichert(true);
    try {
      const daten = { name: r.name.trim(), mahlzeiten: r.mahlzeiten, minuten: zahlOderNull(r.minuten), zubereitung: r.zubereitung, zutaten, schwierigkeit: r.schwierigkeit, budget: r.budget,
        gewuerze: r.gewuerze.split(',').map((x) => x.trim()).filter(Boolean).slice(0, 15),
        zubereitungszeit_min: zahlOderNull(r.zubereitungszeit_min), kochzeit_min: zahlOderNull(r.kochzeit_min), portionen: zahlOderNull(r.portionen) || 1,
        tags: r.tags.split(',').map((x) => x.trim()).filter(Boolean).slice(0, 15) };
      if (vorlage?.id) await ernaehrungAPI.rezeptAendern(vorlage.id, daten); else await ernaehrungAPI.rezeptNeu(daten);
      toast.success(vorlage ? 'Rezept gespeichert' : 'Rezept angelegt'); onGespeichert(); onClose();
    } catch (err) { toast.error(fehlertext(err)); } finally { setSpeichert(false); }
  };
  const einheitenVon = (id) => [{ value: '', label: 'g' }, ...((je[id]?.einheiten || []).map((e) => ({ value: e.name, label: `${e.name} (${e.g} g)` })))];
  return (
    <Dialog open={offen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{vorlage ? 'Rezept bearbeiten' : 'Neues Rezept'}</DialogTitle></DialogHeader>
        <form onSubmit={speichern} className="space-y-3">
          <FormField label="Name" required><Input value={r.name} maxLength={120} onChange={(e) => setR({ ...r, name: e.target.value })} /></FormField>
          <fieldset>
            <legend className="text-xs font-medium mb-1">Passt zu <span className="text-destructive">*</span></legend>
            <div className="flex flex-wrap gap-3">
              {Object.entries(MAHLZEIT).map(([k, label]) => (
                <label key={k} className="flex items-center gap-1.5 text-sm">
                  <input type="checkbox" checked={r.mahlzeiten.includes(k)}
                    onChange={(e) => setR({ ...r, mahlzeiten: e.target.checked ? [...r.mahlzeiten, k] : r.mahlzeiten.filter((m) => m !== k) })} />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset className="space-y-2">
            <legend className="text-xs font-medium">Zutaten (Grundmenge für {r.portionen && Number(r.portionen) !== 1 ? `${r.portionen} Portionen` : 'eine Portion'})</legend>
            {r.zutaten.map((z, i) => (
              <div key={i} className="space-y-1 rounded-md border p-2">
                <div className="flex gap-2">
                  <NativeSelect aria-label={`Zutat ${i + 1}`} value={z.lebensmittel_id} onChange={(v) => setR((x) => ({ ...x, zutaten: x.zutaten.map((y, j) => (j === i ? { ...y, lebensmittel_id: v, einheit: '' } : y)) }))} options={optionen} placeholder="Lebensmittel …" />
                  <Input aria-label={`Menge Zutat ${i + 1}`} type="number" min="0.25" step="any" max="2000" className="w-24" placeholder={z.einheit || 'g'} value={z.gramm} onChange={(e) => zutat(i, 'gramm', e.target.value)} />
                  {/* Haushaltsmaß (E0): nur die, die das Lebensmittel kennt — der Server rechnet in Gramm um */}
                  <NativeSelect aria-label={`Einheit Zutat ${i + 1}`} className="w-32" value={z.einheit} onChange={(v) => zutat(i, 'einheit', v)} options={einheitenVon(z.lebensmittel_id)} />
                  <Button type="button" variant="ghost" size="icon" aria-label={`Zutat ${i + 1} entfernen`} disabled={r.zutaten.length === 1}
                    onClick={() => setR({ ...r, zutaten: r.zutaten.filter((_, j) => j !== i) })}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                  <label className="flex items-center gap-1" title="Skaliert nicht mit, wenn das Rezept auf ein Kalorienziel gebracht wird (z. B. 1 EL Sojasoße)">
                    <input type="checkbox" checked={!!z.gesperrt} onChange={(e) => zutat(i, 'gesperrt', e.target.checked)} />feste Menge
                  </label>
                  <span className="text-muted-foreground">Alternativen:</span>
                  {(z.alternativen || []).map((a) => (
                    <Badge key={a} variant="secondary" className="font-normal gap-1">{je[a]?.name || a}<button type="button" aria-label={`Alternative ${je[a]?.name || a} entfernen`} onClick={() => zutat(i, 'alternativen', z.alternativen.filter((x) => x !== a))}><X className="w-3 h-3" /></button></Badge>
                  ))}
                  {(z.alternativen || []).length < 5 && (
                    <NativeSelect aria-label={`Alternative für Zutat ${i + 1} hinzufügen`} className="h-7 w-44 text-xs" value="" placeholder="+ Alternative …"
                      onChange={(v) => { if (v && v !== z.lebensmittel_id && !(z.alternativen || []).includes(v)) zutat(i, 'alternativen', [...(z.alternativen || []), v]); }}
                      options={optionen.filter((o) => o.value !== z.lebensmittel_id)} />
                  )}
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setR({ ...r, zutaten: [...r.zutaten, LEER_ZUTAT] })}><Plus className="w-4 h-4 mr-1" />Zutat</Button>
          </fieldset>
          <div aria-live="polite">
            <p className="text-xs text-muted-foreground mb-1">Nährwerte der Grundmenge</p><MakroKacheln n={live} klein />
            {livePreis && <p className="text-xs mt-1"><Preis p={livePreis} davor="Preis der Grundmenge: " /></p>}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <FormField label="Vorbereitung (Min.)"><Input type="number" min="0" max="600" value={r.zubereitungszeit_min} onChange={(e) => setR({ ...r, zubereitungszeit_min: e.target.value })} /></FormField>
            <FormField label="Kochen (Min.)"><Input type="number" min="0" max="600" value={r.kochzeit_min} onChange={(e) => setR({ ...r, kochzeit_min: e.target.value })} /></FormField>
            <FormField label="Gesamt (Min.)" hint="leer = Summe"><Input type="number" min="0" max="600" value={r.minuten} onChange={(e) => setR({ ...r, minuten: e.target.value })} /></FormField>
            <FormField label="Schwierigkeit"><NativeSelect value={r.schwierigkeit} onChange={(v) => setR({ ...r, schwierigkeit: v })} options={Object.entries(SCHWIERIGKEIT).map(([value, label]) => ({ value, label }))} /></FormField>
            <FormField label="Budget"><NativeSelect value={r.budget} onChange={(v) => setR({ ...r, budget: v })} options={Object.entries(BUDGET_NAME).map(([value, label]) => ({ value, label }))} /></FormField>
            <FormField label="Portionen (Grundmenge)"><Input type="number" min="0.5" step="0.5" max="12" value={r.portionen} onChange={(e) => setR({ ...r, portionen: e.target.value })} /></FormField>
          </div>
          <FormField label="Tags" hint="Mit Komma getrennt, z. B. high protein, meal-prep — für Suche und Kochbuch-Filter."><Input value={r.tags} maxLength={400} onChange={(e) => setR({ ...r, tags: e.target.value })} /></FormField>
          <FormField label="Zubereitung" hint="Ein Schritt pro Zeile — erscheint nummeriert. Keine Mengen nennen, die passen sich dem Kalorienziel an."><textarea className="w-full min-h-[120px] rounded-md border border-input bg-background px-3 py-2 text-sm" maxLength={3000} value={r.zubereitung} onChange={(e) => setR({ ...r, zubereitung: e.target.value })} /></FormField>
          <FormField label="Gewürze nach Belieben" hint="Mit Komma getrennt, z. B. Salz, Pfeffer, Paprikapulver — ohne Nährwerte, nicht auf der Einkaufsliste."><Input value={r.gewuerze} maxLength={400} onChange={(e) => setR({ ...r, gewuerze: e.target.value })} /></FormField>
          <DialogFooter><Button type="submit" disabled={speichert}>{speichert && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Speichern</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const LEER_LM = { name: '', kcal: '', eiweiss: '', fett: '', kh: '', preis_kg: '', merkmale: [], basis: 'g', einheiten: [], allergene: [], eigenschaften: [] };
const PREIS_KG_MAX = 500;
const PREIS_FEHLER = `Preis je kg: eine Zahl größer als 0 und höchstens ${PREIS_KG_MAX} — oder leer lassen`;
const preisUngueltig = (p) => Number.isNaN(p) || (p !== null && (p <= 0 || p > PREIS_KG_MAX));
const haken = (liste, wert, an) => (an ? [...liste, wert] : liste.filter((x) => x !== wert));

function LebensmittelDialog({ offen, onClose, onGespeichert }) {
  const [l, setL] = useState(LEER_LM);
  const [speichert, setSpeichert] = useState(false);
  const [opt, setOpt] = useState(null);           // E0: feste Listen (Allergene, Eigenschaften, Haushaltsmaße) vom Server
  useEffect(() => { if (offen) setL(LEER_LM); }, [offen]);
  useEffect(() => { if (offen && !opt) ernaehrungAPI.optionen().then((r) => setOpt(r.data)).catch(() => {}); }, [offen, opt]);
  const masse = Object.keys(opt?.haushaltsmasse || {});
  const speichern = async (e) => {
    e.preventDefault();
    if (!l.name.trim() || l.kcal === '') { toast.error('Name und kcal sind Pflicht'); return; }
    const preis = preisZahl(l.preis_kg);
    if (preisUngueltig(preis)) { toast.error(PREIS_FEHLER); return; }
    const einheiten = l.einheiten.map((x) => ({ name: x.name, g: Number(String(x.g || '').replace(',', '.')) }));
    if (einheiten.some((x) => !x.name || !(x.g > 0))) { toast.error('Jedes Haushaltsmaß braucht einen Grammwert größer 0'); return; }
    setSpeichert(true);
    try {
      await ernaehrungAPI.lebensmittelNeu({ name: l.name.trim(), merkmale: l.merkmale, ...(preis !== null ? { preis_kg: preis } : {}),
        basis: l.basis, einheiten, allergene: l.allergene, eigenschaften: l.eigenschaften,
        ...Object.fromEntries(['kcal', 'eiweiss', 'fett', 'kh'].map((k) => [k, Number(String(l[k] || 0).replace(',', '.'))])) });
      toast.success('Lebensmittel angelegt'); onGespeichert(); onClose();
    } catch (err) { toast.error(fehlertext(err)); } finally { setSpeichert(false); }
  };
  return (
    <Dialog open={offen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Neues Lebensmittel</DialogTitle></DialogHeader>
        <form onSubmit={speichern} className="space-y-3">
          <FormField label="Name" required><Input value={l.name} maxLength={120} onChange={(e) => setL({ ...l, name: e.target.value })} /></FormField>
          <p className="text-xs text-muted-foreground">Nährwerte je 100 g, am besten von der Packung.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[['kcal', 'kcal'], ['eiweiss', 'Eiweiß g'], ['fett', 'Fett g'], ['kh', 'KH g']].map(([k, label]) => (
              <FormField key={k} label={label} required={k === 'kcal'}><Input inputMode="decimal" value={l[k]} onChange={(e) => setL({ ...l, [k]: e.target.value })} /></FormField>
            ))}
          </div>
          <FormField label="Preis je kg (€)" hint="Freiwillig. Umrechnen: 500 g für 1,49 € sind 2,98 € je kg. Leer = Preis unbekannt — Gerichte damit zeigen dann keinen Betrag.">
            <Input inputMode="decimal" className="w-32" placeholder="z. B. 2,98" value={l.preis_kg} onChange={(e) => setL({ ...l, preis_kg: e.target.value })} />
          </FormField>
          <fieldset>
            <legend className="text-xs font-medium mb-1">Enthält</legend>
            <div className="flex flex-wrap gap-3">
              {MERKMALE.map((m) => (
                <label key={m} className="flex items-center gap-1.5 text-sm">
                  <input type="checkbox" checked={l.merkmale.includes(m)}
                    onChange={(e) => setL({ ...l, merkmale: e.target.checked ? [...l.merkmale, m] : l.merkmale.filter((x) => x !== m) })} />
                  {m}
                </label>
              ))}
            </div>
          </fieldset>
          {/* E0 (15.10.2026): Basis, Haushaltsmaße mit Grammwert, EU-Allergene, Eigenschaften — Listen aus /optionen */}
          <div className="grid grid-cols-[6rem_1fr] gap-2 items-start">
            <FormField label="Basis">
              <select value={l.basis} onChange={(e) => setL({ ...l, basis: e.target.value })} className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm" aria-label="Basis">
                <option value="g">100 g</option><option value="ml">100 ml</option>
              </select>
            </FormField>
            <FormField label="Haushaltsmaße" hint="Stück, EL, Tasse … mit Grammwert — das Mitglied loggt dann „2 EL“ statt Gramm.">
              <div className="space-y-1.5">
                {l.einheiten.map((x, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <select value={x.name} onChange={(e) => setL({ ...l, einheiten: l.einheiten.map((y, j) => (j === i ? { ...y, name: e.target.value } : y)) })} className="h-8 rounded-md border border-input bg-background px-2 text-sm" aria-label="Haushaltsmaß">
                      {masse.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <Input inputMode="decimal" className="h-8 w-20" placeholder="g" value={x.g} onChange={(e) => setL({ ...l, einheiten: l.einheiten.map((y, j) => (j === i ? { ...y, g: e.target.value } : y)) })} aria-label="Gramm" />
                    <span className="text-xs text-muted-foreground">{l.basis}</span>
                    <Button type="button" size="sm" variant="ghost" className="h-8 px-2" onClick={() => setL({ ...l, einheiten: l.einheiten.filter((_, j) => j !== i) })} aria-label="Haushaltsmaß entfernen">×</Button>
                  </div>
                ))}
                {l.einheiten.length < 8 && masse.length > 0 && (
                  <Button type="button" size="sm" variant="outline" className="h-8" onClick={() => {
                    const frei = masse.find((m) => !l.einheiten.some((x) => x.name === m)) || masse[0];
                    setL({ ...l, einheiten: [...l.einheiten, { name: frei, g: opt?.haushaltsmasse?.[frei] ?? '' }] });
                  }}>+ Haushaltsmaß</Button>
                )}
              </div>
            </FormField>
          </div>
          <fieldset>
            <legend className="text-xs font-medium mb-1">Allergene (EU-Kennzeichnung)</legend>
            <div className="flex flex-wrap gap-x-3 gap-y-1">
              {Object.entries(opt?.allergene || {}).map(([k, label]) => (
                <label key={k} className="flex items-center gap-1.5 text-sm">
                  <input type="checkbox" checked={l.allergene.includes(k)} onChange={(e) => setL({ ...l, allergene: haken(l.allergene, k, e.target.checked) })} />{label}
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="text-xs font-medium mb-1">Eigenschaften</legend>
            <div className="flex flex-wrap gap-3">
              {Object.entries(opt?.eigenschaften || {}).map(([k, label]) => (
                <label key={k} className="flex items-center gap-1.5 text-sm">
                  <input type="checkbox" checked={l.eigenschaften.includes(k)} onChange={(e) => setL({ ...l, eigenschaften: haken(l.eigenschaften, k, e.target.checked) })} />{label}
                </label>
              ))}
            </div>
          </fieldset>
          <DialogFooter><Button type="submit" disabled={speichert}>{speichert && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Speichern</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* Preis je kg in der Zeile ändern (11.10.2026): leer speichern = Preis unbekannt. Ein geänderter Preis gilt als
   Preis des Studios und wird von „P1-Startbestand ergänzen“ nicht mehr angefasst. */
function PreisZeile({ l, onNeu }) {
  const [wert, setWert] = useState(preisEingabe(l.preis_kg));
  const [speichert, setSpeichert] = useState(false);
  useEffect(() => { setWert(preisEingabe(l.preis_kg)); }, [l.preis_kg]);
  const neu = preisZahl(wert);
  const geaendert = neu !== (l.preis_kg ?? null);
  const speichern = async (e) => {
    e.preventDefault();
    if (!geaendert) return;
    if (preisUngueltig(neu)) { toast.error(PREIS_FEHLER); return; }
    setSpeichert(true);
    try {
      const { data } = await ernaehrungAPI.lebensmittelAendern(l.id, { preis_kg: neu });
      // Der Server rundet auf Cent: Feld und Meldung zeigen, was wirklich gespeichert ist („2,984“ wird „2,98“) —
      // sonst bliebe die Zeile „geändert“, wenn der gerundete Wert dem alten gleicht
      setWert(preisEingabe(data.preis_kg));
      toast.success(data.preis_kg ? `${l.name}: ${euro(data.preis_kg)} je kg` : `${l.name}: Preis entfernt`);
      onNeu();
    } catch (err) { toast.error(fehlertext(err)); } finally { setSpeichert(false); }
  };
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
      <div className="min-w-0 flex-1 basis-48">
        <p className="text-sm font-medium break-words">{l.name}{l.aktiv === false ? ' (abgeschaltet)' : ''}</p>
        <p className="text-xs text-muted-foreground tabular-nums">
          {l.kcal} kcal je 100 g
          {l.preis_kg ? (l.preis_quelle === 'richtwert' ? ' · Richtpreis' : ' · euer Preis') : <span className="text-amber-700"> · kein Preis</span>}
        </p>
      </div>
      <form onSubmit={speichern} className="flex items-center gap-1.5">
        <label htmlFor={`preis-${l.id}`} className="sr-only">Preis je kg für {l.name} in Euro</label>
        <Input id={`preis-${l.id}`} inputMode="decimal" autoComplete="off" className="w-24 text-right tabular-nums" placeholder="–" value={wert} onChange={(e) => setWert(e.target.value)} />
        <span className="text-xs text-muted-foreground whitespace-nowrap">€ je kg</span>
        <Button type="submit" size="icon" variant={geaendert ? 'default' : 'ghost'} className="h-8 w-8" disabled={!geaendert || speichert} aria-label={`Preis für ${l.name} speichern`}>
          {speichert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        </Button>
      </form>
    </li>
  );
}

function LebensmittelPreise({ lebensmittel, stand, onNeu }) {
  const [q, setQ] = useState('');
  const [nurOhne, setNurOhne] = useState(false);
  const ohne = lebensmittel.filter((l) => !l.preis_kg).length;
  const liste = useMemo(() => {
    const t = q.trim().toLowerCase();
    return lebensmittel.filter((l) => (!t || l.name.toLowerCase().includes(t)) && (!nurOhne || !l.preis_kg));
  }, [lebensmittel, q, nurOhne]);
  return (
    <section aria-labelledby="lm-preise-titel" className="rounded-md border">
      <div className="p-3 space-y-2 border-b">
        <h2 id="lm-preise-titel" className="text-sm font-semibold">Lebensmittel und Preise ({lebensmittel.length})</h2>
        <p className="text-xs text-muted-foreground">
          Preis je Kilogramm, so wie die Zutat im Rezept steht (trocken, abgetropft, wie gekauft). Daraus rechnet das CRM die „ca.“-Preise je Gericht, Tag, Plan und Einkaufsliste —
          immer mit dem Preis von heute, auch in laufenden Plänen. „Richtpreis“ ist ein mitgelieferter Schätzwert für Eigenmarken (Stand {stand || '–'}); tragt eure Preise ein.
          Leer speichern heißt „Preis unbekannt“.
        </p>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="relative w-full sm:w-72">
            <label htmlFor="lm-preise-suche" className="sr-only">Lebensmittel suchen</label>
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input id="lm-preise-suche" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Lebensmittel suchen …" className="pl-8" autoComplete="off" />
          </div>
          <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={nurOhne} onChange={(e) => setNurOhne(e.target.checked)} />nur ohne Preis ({ohne})</label>
        </div>
      </div>
      {liste.length === 0 ? <p className="p-3 text-sm text-muted-foreground">{lebensmittel.length ? 'Kein Lebensmittel passt.' : 'Noch keine Lebensmittel.'}</p> : (
        <ul className="divide-y max-h-[28rem] overflow-y-auto">
          {liste.map((l) => <PreisZeile key={l.id} l={l} onNeu={onNeu} />)}
        </ul>
      )}
    </section>
  );
}

/* Lizenzhinweis Open Food Facts (ODbL): Pflicht überall, wo Werte von dort angezeigt werden */
/* Quellenangaben (Lizenzpflicht) — überall, wo Werte aus diesen Quellen angezeigt werden:
   BLS 4.0 (CC BY 4.0, Max Rubner-Institut nennen) und Open Food Facts (ODbL). */
export function BlsHinweis({ className = '' }) {
  return (
    <p className={`text-xs text-muted-foreground ${className}`}>
      Nährwerte: <a href="https://www.blsdb.de/" target="_blank" rel="noreferrer" className="underline underline-offset-2">Bundeslebensmittelschlüssel (BLS) 4.0</a>,
      {' '}Max Rubner-Institut (2025), <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer" className="underline underline-offset-2">CC BY 4.0</a>.
    </p>
  );
}

export function OffHinweis({ className = '' }) {
  return (
    <p className={`text-xs text-muted-foreground ${className}`}>
      Nährwerte der mit (OFF) markierten Zutaten: <a href="https://openfoodfacts.org" target="_blank" rel="noreferrer" className="underline underline-offset-2">Open Food Facts</a>,
      {' '}<a href="https://opendatacommons.org/licenses/odbl/1-0/" target="_blank" rel="noreferrer" className="underline underline-offset-2">ODbL 1.0</a> — Gemeinschaftsdaten ohne Gewähr.
    </p>
  );
}

export function Quellen({ bls, off }) {
  return <>{bls && <BlsHinweis />}{off && <OffHinweis />}</>;
}

function BlsDialog({ offen, onClose, onGespeichert }) {
  const [q, setQ] = useState('');
  const [treffer, setTreffer] = useState(null);
  const [wahl, setWahl] = useState(null);
  const [name, setName] = useState('');
  const [merkmale, setMerkmale] = useState([]);
  const [speichert, setSpeichert] = useState(false);
  useEffect(() => { if (offen) { setQ(''); setTreffer(null); setWahl(null); } }, [offen]);
  useEffect(() => {
    if (q.trim().length < 2) { setTreffer(null); return undefined; }
    const t = setTimeout(() => {
      ernaehrungAPI.blsSuche(q.trim()).then((r) => setTreffer(r.data.treffer)).catch((e) => { toast.error(fehlertext(e)); setTreffer([]); });
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const waehlen = (e) => { setWahl(e); setName(e.name.length > 60 ? e.name.slice(0, 60) : e.name); setMerkmale(e.merkmale_vorschlag); };
  const uebernehmen = async () => {
    setSpeichert(true);
    try {
      await ernaehrungAPI.blsUebernehmen({ code: wahl.code, name, merkmale });
      toast.success('In die Studio-Liste übernommen'); onGespeichert(); onClose();
    } catch (err) { toast.error(fehlertext(err)); } finally { setSpeichert(false); }
  };
  const n = (v) => (v === null || v === undefined ? '–' : String(v).replace('.', ','));
  return (
    <Dialog open={offen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Aus dem Bundeslebensmittelschlüssel</DialogTitle></DialogHeader>
        {!wahl ? (
          <div className="space-y-2">
            <FormField label="Suchen (z. B. „Hähnchen Brust roh“, „Linse rot“)"><Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" /></FormField>
            {treffer === null ? <p className="text-xs text-muted-foreground">7.140 Lebensmittel und Gerichte mit amtlichen Nährwerten je 100 g.</p>
              : treffer.length === 0 ? <p className="text-sm text-muted-foreground">Nichts gefunden — anders schreiben oder von Hand anlegen.</p> : (
              <ul className="divide-y rounded-md border max-h-[50vh] overflow-y-auto">
                {treffer.map((e) => (
                  <li key={e.code}>
                    <button type="button" disabled={!!e.uebernommen_als} onClick={() => waehlen(e)}
                      className="w-full text-left px-3 py-2 hover:bg-accent/40 focus:bg-accent/40 focus:outline-none disabled:opacity-60">
                      <p className="text-sm font-medium">{e.name}</p>
                      <p className="text-xs text-muted-foreground">{e.gruppe} · {n(e.kcal)} kcal · E {n(e.eiweiss)} · F {n(e.fett)} · KH {n(e.kh)}{e.uebernommen_als ? ' · schon in der Studio-Liste' : ''}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">BLS {wahl.code}: {wahl.name}</p>
            <FormField label="Name im CRM"><Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></FormField>
            <p className="text-xs text-muted-foreground">je 100 g: {n(wahl.kcal)} kcal · E {n(wahl.eiweiss)} g · F {n(wahl.fett)} g · KH {n(wahl.kh)} g{wahl.ballast !== null ? ` · Ballaststoffe ${n(wahl.ballast)} g` : ''}{wahl.laktose !== null ? ` · Lactose ${n(wahl.laktose)} g` : ''}</p>
            {wahl.fehlt.length > 0 && <p className="text-sm text-destructive" role="alert">Im BLS fehlt: {wahl.fehlt.join(', ')} — so nicht übernehmbar.</p>}
            <fieldset>
              <legend className="text-xs font-medium mb-1">Enthält — bitte prüfen</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {MERKMALE.map((m) => (
                  <label key={m} className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={merkmale.includes(m)} onChange={(e) => setMerkmale(e.target.checked ? [...merkmale, m] : merkmale.filter((x) => x !== m))} />
                    {m}
                  </label>
                ))}
              </div>
              {wahl.gruende.length > 0 && <p className="text-xs text-muted-foreground mt-1">Vorgeschlagen: {wahl.gruende.join(' · ')}</p>}
            </fieldset>
            <BlsHinweis />
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setWahl(null)}>Zurück</Button>
              <Button onClick={uebernehmen} disabled={speichert || wahl.fehlt.length > 0 || !name.trim()}>{speichert && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Übernehmen</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const zutatText = (z) => `${z.name} ${z.gramm} g${z.quelle === 'openfoodfacts' ? ' (OFF)' : ''}`;

function BarcodeDialog({ offen, onClose, onGespeichert }) {
  const [code, setCode] = useState('');
  const [sucht, setSucht] = useState(false);
  const [antwort, setAntwort] = useState(null);
  const [name, setName] = useState('');
  const [merkmale, setMerkmale] = useState([]);
  const [speichert, setSpeichert] = useState(false);
  useEffect(() => { if (offen) { setCode(''); setAntwort(null); } }, [offen]);
  const abrufen = async (e) => {
    e.preventDefault();
    setSucht(true); setAntwort(null);
    try {
      const { data } = await ernaehrungAPI.offVorschau(code);
      setAntwort(data);
      if (data.vorschlag) { setName(data.vorschlag.marke ? `${data.vorschlag.name} (${data.vorschlag.marke})` : data.vorschlag.name); setMerkmale(data.vorschlag.merkmale_vorschlag); }
    } catch (err) { toast.error(fehlertext(err, 'Abruf nicht möglich')); } finally { setSucht(false); }
  };
  const uebernehmen = async () => {
    setSpeichert(true);
    try {
      await ernaehrungAPI.offUebernehmen({ barcode: antwort.vorschlag.barcode, name, merkmale });
      toast.success('Lebensmittel übernommen'); onGespeichert(); onClose();
    } catch (err) { toast.error(fehlertext(err)); } finally { setSpeichert(false); }
  };
  const v = antwort?.vorschlag;
  return (
    <Dialog open={offen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Lebensmittel per Barcode</DialogTitle></DialogHeader>
        <form onSubmit={abrufen} className="flex gap-2 items-end">
          <FormField label="Barcode (EAN)" className="flex-1"><Input inputMode="numeric" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} placeholder="z. B. 4001234567890" /></FormField>
          <Button type="submit" disabled={sucht || code.replace(/\s/g, '').length < 8}>{sucht ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Abrufen'}</Button>
        </form>
        {antwort?.vorhanden && <p className="text-sm" role="status">Schon übernommen als „{antwort.vorhanden.name}“.</p>}
        {v && (
          <div className="space-y-3">
            <FormField label="Name im CRM"><Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} /></FormField>
            <p className="text-xs text-muted-foreground">{v.menge ? `Packung ${v.menge} · ` : ''}je 100 g: {v.kcal ?? '–'} kcal · E {v.eiweiss ?? '–'} g · F {v.fett ?? '–'} g · KH {v.kh ?? '–'} g</p>
            {v.fehlt.length > 0 && <p className="text-sm text-destructive" role="alert">Bei Open Food Facts fehlt: {v.fehlt.join(', ')} — so nicht übernehmbar, bitte von Hand anlegen.</p>}
            <fieldset>
              <legend className="text-xs font-medium mb-1">Enthält — bitte mit der Packung prüfen</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {MERKMALE.map((m) => (
                  <label key={m} className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={merkmale.includes(m)} onChange={(e) => setMerkmale(e.target.checked ? [...merkmale, m] : merkmale.filter((x) => x !== m))} />
                    {m}
                  </label>
                ))}
              </div>
              {v.gruende.length > 0 && <p className="text-xs text-muted-foreground mt-1">Vorgeschlagen: {v.gruende.join(' · ')}</p>}
              {v.spuren.length > 0 && <p className="text-xs text-muted-foreground">Kann Spuren enthalten: {v.spuren.join(', ')}</p>}
              {v.unsicher.length > 0 && <p className="text-xs text-amber-700">Unsicher laut Open Food Facts: {v.unsicher.join(', ')}</p>}
            </fieldset>
            <p className="text-xs text-muted-foreground">
              Quelle: <a href={v.url} target="_blank" rel="noreferrer" className="underline underline-offset-2 inline-flex items-center gap-0.5">Produktseite bei Open Food Facts<ExternalLink className="w-3 h-3" aria-hidden="true" /></a> (ODbL 1.0). Gemeinschaftsdaten ohne Gewähr — nicht für medizinische Zwecke.
            </p>
            <DialogFooter><Button onClick={uebernehmen} disabled={speichert || v.fehlt.length > 0 || !name.trim()}>{speichert && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Übernehmen</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const ERNAEHRUNGSWEISE = { vegetarisch: 'vegetarisch', vegan: 'vegan', laktosefrei: 'laktosefrei', glutenfrei: 'glutenfrei', ohne_nuesse: 'ohne Nüsse', ohne_erdnuss: 'ohne Erdnuss', ohne_fisch: 'ohne Fisch', ohne_schwein: 'ohne Schwein' };

/* Rezeptvorschläge per KI: an das Modell gehen nur Mahlzeit, Ernährungsweise und der Wunsch — nie eine Person.
   Vorschläge landen ungeprüft in der Rezeptliste und kommen erst nach „Übernehmen“ in Pläne. */
export function VorschlagDialog({ offen, onClose, vorgabe, onFertig }) {
  const [f, setF] = useState({ mahlzeit: '', ausschluesse: [], wunsch: '', anzahl: '3' });
  const [laeuft, setLaeuft] = useState(false);
  const [stand, setStand] = useState(null);
  const [ergebnis, setErgebnis] = useState(null);
  useEffect(() => {
    if (!offen) return;
    setF({ mahlzeit: vorgabe?.mahlzeit || '', ausschluesse: vorgabe?.ausschluesse || [], wunsch: '', anzahl: '3' });
    setErgebnis(null);
    ernaehrungAPI.vorschlaegeStatus().then((r) => setStand(r.data)).catch(() => setStand({ verfuegbar: false, grund: 'Status nicht abrufbar' }));
  }, [offen, vorgabe]);
  const anfordern = async (e) => {
    e.preventDefault();
    setLaeuft(true);
    try {
      const { data } = await ernaehrungAPI.vorschlaege({ mahlzeit: f.mahlzeit || null, ausschluesse: f.ausschluesse, wunsch: f.wunsch, anzahl: Number(f.anzahl) });
      setErgebnis(data);
      if (data.angelegt.length) { toast.success(`${data.angelegt.length} Vorschlag/Vorschläge in der Rezeptliste — bitte prüfen`); onFertig?.(); }
    } catch (err) { toast.error(fehlertext(err, 'Vorschläge gerade nicht möglich')); } finally { setLaeuft(false); }
  };
  return (
    <Dialog open={offen} onOpenChange={(o) => !o && !laeuft && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Rezepte vorschlagen lassen</DialogTitle></DialogHeader>
        {stand && !stand.verfuegbar ? (
          <p className="text-sm text-muted-foreground" role="status">{stand.grund}</p>
        ) : (
          <form onSubmit={anfordern} className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <FormField label="Mahlzeit"><NativeSelect value={f.mahlzeit} onChange={(v) => setF({ ...f, mahlzeit: v })} placeholder="beliebig" options={Object.entries(MAHLZEIT).map(([value, label]) => ({ value, label }))} /></FormField>
              <FormField label="Anzahl"><NativeSelect value={f.anzahl} onChange={(v) => setF({ ...f, anzahl: v })} options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }))} /></FormField>
            </div>
            <fieldset>
              <legend className="text-xs font-medium mb-1">Ernährungsweise</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                {Object.entries(ERNAEHRUNGSWEISE).map(([k, label]) => (
                  <label key={k} className="flex items-center gap-1.5 text-sm">
                    <input type="checkbox" checked={f.ausschluesse.includes(k)}
                      onChange={(e) => setF({ ...f, ausschluesse: e.target.checked ? [...f.ausschluesse, k] : f.ausschluesse.filter((x) => x !== k) })} />
                    {label}
                  </label>
                ))}
              </div>
            </fieldset>
            <FormField label="Wunsch (optional)">
              <Input value={f.wunsch} maxLength={200} placeholder="z. B. schnell, unter 15 Minuten, meal-prep-tauglich" onChange={(e) => setF({ ...f, wunsch: e.target.value })} />
            </FormField>
            <p className="text-xs text-muted-foreground">Keine Namen oder Gesundheitsangaben einer Person eintragen — der Wunsch geht an das Sprachmodell. Zutaten kommen nur aus eurer Lebensmittelliste, die Nährwerte rechnet das CRM.</p>
            {ergebnis && (
              <div className="text-sm space-y-1" role="status">
                <p>{ergebnis.angelegt.length ? `Neu zu prüfen: ${ergebnis.angelegt.map((r) => r.name).join(', ')}` : 'Kein brauchbarer Vorschlag.'}</p>
                {ergebnis.verworfen.length > 0 && <p className="text-xs text-muted-foreground">Verworfen: {ergebnis.verworfen.join(' · ')}</p>}
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={laeuft || stand === null}>{laeuft ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}{laeuft ? 'Denkt nach …' : 'Vorschlagen'}</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const AUSSCHLUSS_NAME = { vegetarisch: 'vegetarisch', vegan: 'vegan', laktosefrei: 'laktosefrei', glutenfrei: 'glutenfrei', ohne_nuesse: 'ohne Nüsse', ohne_erdnuss: 'ohne Erdnuss', ohne_fisch: 'ohne Fisch', ohne_schwein: 'ohne Schwein' };
const ZEIT_FILTER = [['', 'jede Dauer'], ['10', 'bis 10 Min.'], ['20', 'bis 20 Min.'], ['30', 'bis 30 Min.']];
const KCAL_FILTER = [['', 'alle kcal'], ['0-300', 'bis 300 kcal'], ['300-600', '300–600 kcal'], ['600-9999', 'über 600 kcal']];

export const SCHWIERIGKEIT = { einfach: 'Einfach', mittel: 'Mittel', aufwendig: 'Aufwendig' };
export const BUDGET_NAME = { preiswert: 'Preiswert', mittel: 'Mittel', premium: 'Premium' };

function RezeptKarte({ r, onUmschalten, onNeu, onOeffne, onBearbeiten }) {
  const n = r.naehrwerte;
  const summe = (n.eiweiss * 4 + n.kh * 4 + n.fett * 9) || 1;
  const [laedt, setLaedt] = useState(false);
  const hochladen = async (e) => {
    const datei = e.target.files?.[0];
    e.target.value = '';
    if (!datei) return;
    setLaedt(true);
    try { await ernaehrungAPI.rezeptBild(r.id, datei); toast.success('Foto gespeichert'); onNeu(); }
    catch (err) { toast.error(fehlertext(err)); } finally { setLaedt(false); }
  };
  const entfernen = async () => {
    try { await ernaehrungAPI.rezeptBildWeg(r.id); onNeu(); } catch (err) { toast.error(fehlertext(err)); }
  };
  return (
    <Card className={`relative w-full flex flex-col overflow-hidden ${r.aktiv === false ? 'opacity-60' : ''}`}>
      <div className="relative aspect-[16/10] bg-muted">
        {r.bild_url ? <img src={bildSrc(r.bild_url)} alt={r.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
          : <div className="absolute inset-0 flex items-center justify-center text-muted-foreground"><ChefHat className="w-10 h-10 opacity-40" aria-hidden="true" /></div>}
        {r.symbolbild && <span className="absolute right-2 bottom-2 rounded bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground">Symbolbild</span>}
        <span className="absolute left-2 bottom-2 rounded-md bg-background/90 px-2 py-0.5 text-xs font-medium tabular-nums">{n.kcal} kcal | {Math.round(n.eiweiss)} g E | {Math.round(n.kh)} g KH | {Math.round(n.fett)} g F</span>
        <div className="absolute right-2 top-2 z-10 flex gap-1">
          <label className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-background/90 shadow hover:bg-background focus-within:ring-2 focus-within:ring-ring" title={r.bild_url ? 'Foto ersetzen' : 'Foto hinzufügen'}>
            {laedt ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
            <span className="sr-only">{r.bild_url ? `Foto für ${r.name} ersetzen` : `Foto für ${r.name} hinzufügen`}</span>
            <input type="file" accept="image/jpeg,image/png,image/webp" className="sr-only" onChange={hochladen} disabled={laedt} />
          </label>
          {r.bild_url && <button type="button" onClick={entfernen} className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-background/90 shadow hover:bg-background" aria-label={`Foto von ${r.name} entfernen`}><ImageOff className="w-4 h-4" /></button>}
        </div>
      </div>
      <CardContent className="p-3 flex flex-col gap-2 flex-1">
        <div className="flex flex-wrap gap-1">
          {Object.keys(MAHLZEIT).filter((m) => r.mahlzeiten.includes(m)).map((m) => <Badge key={m} variant="secondary" className="font-normal">{MAHLZEIT[m]}</Badge>)}
          {r.herkunft === 'ki' && <Badge variant="outline" className="font-normal"><Sparkles className="w-3 h-3 mr-1" aria-hidden="true" />KI</Badge>}
          {r.aktiv === false && <Badge variant="outline" className="font-normal">abgeschaltet</Badge>}
          {(r.tags || []).map((t) => <Badge key={t} variant="outline" className="font-normal">{t}</Badge>)}
        </div>
        <h3 className="font-semibold leading-snug break-words">
          <button type="button" className="text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm after:absolute after:inset-0 after:content-['']" onClick={() => onOeffne(r)}>{r.name}</button>
        </h3>
        <div className="grid grid-cols-3 gap-1 text-center text-xs border-y py-1.5">
          <div><span className="block text-muted-foreground">Zeit</span><span className="font-medium inline-flex items-center gap-1" title={r.zubereitungszeit_min != null || r.kochzeit_min != null ? `Vorbereitung ${r.zubereitungszeit_min ?? 0} Min. · Kochen ${r.kochzeit_min ?? 0} Min.` : undefined}><Clock className="w-3 h-3" aria-hidden="true" />{r.minuten ? `${r.minuten} Min.` : '–'}</span></div>
          <div><span className="block text-muted-foreground">Schwierigkeit</span><span className="font-medium">{SCHWIERIGKEIT[r.schwierigkeit] || '–'}</span></div>
          <div><span className="block text-muted-foreground">Budget</span><span className="font-medium">{BUDGET_NAME[r.budget] || '–'}</span></div>
        </div>
        <div className="flex h-1.5 rounded-full overflow-hidden bg-muted" role="img" aria-label={`Energie aus Eiweiß ${Math.round((n.eiweiss * 4 / summe) * 100)} %, Kohlenhydraten ${Math.round((n.kh * 4 / summe) * 100)} %, Fett ${Math.round((n.fett * 9 / summe) * 100)} %`}>
          <span className="bg-primary" style={{ width: `${(n.eiweiss * 4 / summe) * 100}%` }} />
          <span className="bg-primary/50" style={{ width: `${(n.kh * 4 / summe) * 100}%` }} />
          <span className="bg-primary/20" style={{ width: `${(n.fett * 9 / summe) * 100}%` }} />
        </div>
        {r.preis && <p className="text-xs"><Preis p={r.preis} danach=" je Portion (Grundmenge)" /></p>}
        <p className="text-xs text-muted-foreground line-clamp-2">{r.zutaten.map(zutatText).join(', ')}</p>
        <div className="mt-auto pt-1 relative z-10 flex flex-wrap gap-1">
          <Button size="sm" variant="outline" className="-ml-0.5" onClick={() => onOeffne(r)}>Rezept &amp; Mengen</Button>
          <Button size="sm" variant="ghost" onClick={() => onBearbeiten(r)}><Pencil className="w-3.5 h-3.5 mr-1" aria-hidden="true" />Bearbeiten</Button>
          <Button size="sm" variant="ghost" onClick={() => onUmschalten(r)}>{r.aktiv === false ? 'Einschalten' : 'Abschalten'}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Rezepte() {
  const [daten, setDaten] = useState(null);
  const [filter, setFilter] = useState({ q: '', mahlzeit: '', zeit: '', kcal: '', passt: '', schwer: '', budget: '', foto: false, aus: false });
  const [ausschlussMerkmale, setAusschlussMerkmale] = useState({});
  useEffect(() => { ernaehrungAPI.optionen().then((r) => setAusschlussMerkmale(r.data.ausschluss_merkmale || {})).catch(() => {}); }, []);
  const [fehler, setFehler] = useState('');
  const [dialog, setDialog] = useState(null);
  const [bearbeitet, setBearbeitet] = useState(null);       // E3: vorhandenes Rezept im Rezeptdialog
  const [legtAn, setLegtAn] = useState(false);
  const [ansicht, setAnsicht] = useState(null);
  const [zeigtPreise, setZeigtPreise] = useState(false);
  const laden = useCallback(async () => {
    setFehler('');
    try { setDaten((await ernaehrungAPI.rezepte()).data); }
    catch (e) { setFehler(fehlertext(e, 'Rezepte konnten nicht geladen werden')); setDaten({ rezepte: [], lebensmittel: [] }); }
  }, []);
  useEffect(() => { laden(); }, [laden]);
  const startbestand = async () => {
    setLegtAn(true);
    try {
      const { data } = await ernaehrungAPI.startbestand();
      toast.success(`${data.rezepte} Rezepte und ${data.lebensmittel} Lebensmittel angelegt${data.auf_bls ? `, ${data.auf_bls} Richtwerte durch BLS-Werte ersetzt` : ''}${data.fotos ? `, ${data.fotos} Fotos ergänzt` : ''}${data.zubereitung ? `, ${data.zubereitung} Zubereitungen in Schritte aufgeteilt` : ''}${data.preise ? `, ${data.preise} Richtpreise eingetragen` : ''}`);
      laden();
    } catch (e) { toast.error(fehlertext(e)); } finally { setLegtAn(false); }
  };
  const umschalten = async (r) => {
    try { await ernaehrungAPI.rezeptAendern(r.id, { aktiv: r.aktiv === false }); laden(); }
    catch (e) { toast.error(fehlertext(e)); }
  };
  const uebernehmen = async (r) => {
    try { await ernaehrungAPI.rezeptUebernehmen(r.id); toast.success(`„${r.name}“ übernommen — kommt ab jetzt in Pläne`); laden(); }
    catch (e) { toast.error(fehlertext(e)); }
  };
  const verwerfen = async (r) => {
    try { await ernaehrungAPI.vorschlagVerwerfen(r.id); toast.success('Vorschlag verworfen'); laden(); }
    catch (e) { toast.error(fehlertext(e)); }
  };
  const offen = daten ? daten.rezepte.filter((r) => r.status === 'vorschlag') : [];
  const fertige = daten ? daten.rezepte.filter((r) => r.status !== 'vorschlag') : [];
  const merkmaleJe = useMemo(() => Object.fromEntries((daten?.lebensmittel || []).map((l) => [l.id, l.merkmale || []])), [daten]);
  const gefiltert = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    const gesperrt = new Set(ausschlussMerkmale[filter.passt] || []);
    const [kMin, kMax] = filter.kcal ? filter.kcal.split('-').map(Number) : [0, Infinity];
    return (daten?.rezepte || []).filter((r) => r.status !== 'vorschlag' && (
      (!q || r.name.toLowerCase().includes(q) || r.zutaten.some((z) => z.name.toLowerCase().includes(q)))
      && (!filter.mahlzeit || r.mahlzeiten.includes(filter.mahlzeit))
      && (!filter.zeit || (r.minuten || 0) <= Number(filter.zeit))
      && r.naehrwerte.kcal >= kMin && r.naehrwerte.kcal < kMax
      && (!filter.passt || !r.zutaten.some((z) => (merkmaleJe[z.lebensmittel_id] || []).some((m) => gesperrt.has(m))))
      && (!filter.schwer || r.schwierigkeit === filter.schwer)
      && (!filter.budget || r.budget === filter.budget)
      && (!filter.foto || r.bild_url)
      && (filter.aus || r.aktiv !== false)
    ));
  }, [daten, filter, merkmaleJe, ausschlussMerkmale]);
  const filterAktiv = filter.q || filter.mahlzeit || filter.zeit || filter.kcal || filter.passt || filter.schwer || filter.budget || filter.foto;
  const abgeschaltet = fertige.filter((r) => r.aktiv === false).length;

  if (daten === null) return <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => { setBearbeitet(null); setDialog('rezept'); }} disabled={!daten.lebensmittel.length}><Plus className="w-4 h-4 mr-1" />Rezept</Button>
        <Button size="sm" variant="outline" onClick={() => setDialog('lm')}><Plus className="w-4 h-4 mr-1" />Lebensmittel</Button>
        <Button size="sm" variant="outline" onClick={() => setDialog('bls')}><Database className="w-4 h-4 mr-1" />Aus BLS</Button>
        <Button size="sm" variant="outline" onClick={() => setDialog('barcode')}><ScanBarcode className="w-4 h-4 mr-1" />Per Barcode</Button>
        <Button size="sm" variant="outline" onClick={() => setDialog('ki')} disabled={!daten.lebensmittel.length}><Sparkles className="w-4 h-4 mr-1" />Vorschlagen lassen</Button>
        <Button size="sm" variant="outline" onClick={startbestand} disabled={legtAn}>{legtAn && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}P1-Startbestand ergänzen</Button>
        <Button size="sm" variant={zeigtPreise ? 'secondary' : 'outline'} aria-expanded={zeigtPreise} onClick={() => setZeigtPreise(!zeigtPreise)} disabled={!daten.lebensmittel.length}><Coins className="w-4 h-4 mr-1" />Lebensmittel &amp; Preise</Button>
      </div>
      {fehler && <p className="text-sm text-destructive" role="alert">{fehler}</p>}
      {daten.lebensmittel.length > 0 && !daten.lebensmittel.some((l) => l.preis_kg) && (
        <p className="text-xs text-muted-foreground">Noch kein Lebensmittel hat einen Preis — „P1-Startbestand ergänzen“ trägt Richtpreise ein, unter „Lebensmittel &amp; Preise“ passt ihr sie an.</p>
      )}
      {zeigtPreise && <LebensmittelPreise lebensmittel={daten.lebensmittel} stand={daten.preise_stand} onNeu={laden} />}
      {daten.lebensmittel.some((l) => l.quelle === 'richtwert') && (
        <p className="text-xs text-muted-foreground">Einige Nährwerte sind noch gerundete Richtwerte — „P1-Startbestand ergänzen“ ersetzt sie durch BLS-Werte.</p>
      )}
      {offen.length > 0 && (
        <section aria-labelledby="vorschlaege-titel" className="space-y-2">
          <h2 id="vorschlaege-titel" className="text-sm font-semibold">Vorschläge zum Prüfen ({offen.length})</h2>
          <ul className="divide-y rounded-md border border-amber-200 bg-amber-500/5">
            {offen.map((r) => (
              <li key={r.id} className="px-3 py-2.5 space-y-1">
                <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium break-words">{r.name} <Badge variant="outline" className="ml-1 bg-amber-500/10 text-amber-700 border-amber-200">Vorschlag</Badge></p>
                    <p className="text-xs text-muted-foreground">{r.mahlzeiten.map((m) => MAHLZEIT[m]).join(', ')}{r.minuten ? ` · ${r.minuten} Min.` : ''} · {r.zutaten.map(zutatText).join(', ')}</p>
                    {r.zubereitung && <p className="text-xs mt-0.5 whitespace-pre-line">{r.zubereitung}</p>}
                    {/* Betrag oder — gerade bei frisch übernommenen Lebensmitteln — wem der Preis fehlt */}
                    {r.preis && <p className="text-xs mt-0.5 text-muted-foreground"><Preis p={r.preis} danach=" je Portion (Grundmenge)" /></p>}
                  </div>
                  <span className="text-xs tabular-nums text-muted-foreground shrink-0">{r.naehrwerte.kcal} kcal · E {r.naehrwerte.eiweiss} · F {r.naehrwerte.fett} · KH {r.naehrwerte.kh}</span>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => uebernehmen(r)}><Check className="w-4 h-4 mr-1" />Übernehmen</Button>
                  <Button size="sm" variant="ghost" onClick={() => verwerfen(r)}><X className="w-4 h-4 mr-1" />Verwerfen</Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
      {fertige.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">
          Noch keine freigegebenen Rezepte. „P1-Startbestand ergänzen“ legt 60 Studio-Rezepte mit Fotos und Zubereitung an, die ihr danach anpasst.
        </CardContent></Card>
      ) : (
        <section aria-label="Rezeptbuch" className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-2 items-end">
            <div className="relative col-span-2 sm:col-span-3 xl:col-span-6">
              <label htmlFor="rezept-suche" className="sr-only">Rezept oder Zutat suchen</label>
              <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" aria-hidden="true" />
              <Input id="rezept-suche" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} placeholder="Rezept oder Zutat suchen …" className="pl-8" />
            </div>
            <NativeSelect aria-label="Mahlzeit" value={filter.mahlzeit} onChange={(v) => setFilter({ ...filter, mahlzeit: v })} options={[{ value: '', label: 'alle Mahlzeiten' }, ...Object.entries(MAHLZEIT).map(([value, label]) => ({ value, label }))]} />
            <NativeSelect aria-label="Zubereitungszeit" value={filter.zeit} onChange={(v) => setFilter({ ...filter, zeit: v })} options={ZEIT_FILTER.map(([value, label]) => ({ value, label }))} />
            <NativeSelect aria-label="Kalorien" value={filter.kcal} onChange={(v) => setFilter({ ...filter, kcal: v })} options={KCAL_FILTER.map(([value, label]) => ({ value, label }))} />
            <NativeSelect aria-label="Passt zu" value={filter.passt} onChange={(v) => setFilter({ ...filter, passt: v })} options={[{ value: '', label: 'jede Ernährung' }, ...Object.entries(AUSSCHLUSS_NAME).map(([value, label]) => ({ value, label }))]} />
            <NativeSelect aria-label="Schwierigkeit" value={filter.schwer} onChange={(v) => setFilter({ ...filter, schwer: v })} options={[{ value: '', label: 'jede Schwierigkeit' }, ...Object.entries(SCHWIERIGKEIT).map(([value, label]) => ({ value, label }))]} />
            <NativeSelect aria-label="Budget" value={filter.budget} onChange={(v) => setFilter({ ...filter, budget: v })} options={[{ value: '', label: 'jedes Budget' }, ...Object.entries(BUDGET_NAME).map(([value, label]) => ({ value, label }))]} />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span aria-live="polite">{gefiltert.length} von {fertige.length} Rezepten</span>
            {abgeschaltet > 0 && (
              <label className="flex items-center gap-1.5"><input type="checkbox" checked={filter.aus} onChange={(e) => setFilter({ ...filter, aus: e.target.checked })} />abgeschaltete zeigen ({abgeschaltet})</label>
            )}
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={filter.foto} onChange={(e) => setFilter({ ...filter, foto: e.target.checked })} />nur mit Foto</label>
            {filterAktiv && <Button size="sm" variant="ghost" onClick={() => setFilter({ ...filter, q: '', mahlzeit: '', zeit: '', kcal: '', passt: '', schwer: '', budget: '', foto: false })}><X className="w-4 h-4 mr-1" />Filter zurücksetzen</Button>}
          </div>
          {gefiltert.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Kein Rezept passt zu diesen Filtern.</p>
          ) : (
            <ul className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {gefiltert.map((r) => <li key={r.id} className="flex"><RezeptKarte r={r} onUmschalten={umschalten} onNeu={laden} onOeffne={(x) => setAnsicht(x.id)} onBearbeiten={(x) => { setBearbeitet(x); setDialog('rezept'); }} /></li>)}
            </ul>
          )}
        </section>
      )}
      <RezeptAnsicht rezeptId={ansicht} onClose={() => setAnsicht(null)} laden={ernaehrungAPI.rezeptPortion} />
      <RezeptDialog offen={dialog === 'rezept'} onClose={() => { setDialog(null); setBearbeitet(null); }} lebensmittel={daten.lebensmittel} onGespeichert={laden} vorlage={bearbeitet} />
      <LebensmittelDialog offen={dialog === 'lm'} onClose={() => setDialog(null)} onGespeichert={laden} />
      <VorschlagDialog offen={dialog === 'ki'} onClose={() => setDialog(null)} onFertig={laden} />
      <BarcodeDialog offen={dialog === 'barcode'} onClose={() => setDialog(null)} onGespeichert={laden} />
      <BlsDialog offen={dialog === 'bls'} onClose={() => setDialog(null)} onGespeichert={laden} />
      <Quellen bls={daten.lebensmittel.some((l) => l.quelle === 'bls')} off={daten.lebensmittel.some((l) => l.quelle === 'openfoodfacts')} />
    </div>
  );
}

export const COACHING_ZUSTAND = {
  offen: { label: 'wartet auf Coach', cls: 'bg-amber-500/10 text-amber-700 border-amber-200' },
  laeuft: { label: 'läuft', cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-200' },
  beendet: { label: 'beendet', cls: 'bg-muted text-muted-foreground' },
  storniert: { label: 'storniert', cls: 'bg-muted text-muted-foreground' },
  keins: { label: 'nicht gebucht', cls: 'bg-muted text-muted-foreground' },
};

function UebernehmenDialog({ buchung, onClose, onFertig }) {
  const [f, setF] = useState({ datum: '', start: '17:00', ende: '17:30' });
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => {
    if (!buchung) return;
    const d = new Date();
    setF({ datum: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`, start: '17:00', ende: '17:30' });
  }, [buchung]);
  const senden = async (e) => {
    e.preventDefault();
    setLaeuft(true);
    try {
      await ernaehrungAPI.coachingUebernehmen(buchung.id, f);
      toast.success(`Übernommen — Erstgespräch am ${datum(f.datum)} um ${f.start}`);
      onFertig(); onClose();
    } catch (err) { toast.error(fehlertext(err)); onFertig(); } finally { setLaeuft(false); }
  };
  return (
    <Dialog open={!!buchung} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Coaching übernehmen</DialogTitle></DialogHeader>
        {buchung && (
          <form onSubmit={senden} className="space-y-3">
            <p className="text-sm">{buchung.member_name} · {buchung.produkt} · {buchung.wochen} Wochen</p>
            <div className="grid grid-cols-3 gap-2">
              <FormField label="Erstgespräch am" className="col-span-3 sm:col-span-1"><Input type="date" required value={f.datum} onChange={(e) => setF({ ...f, datum: e.target.value })} /></FormField>
              <FormField label="von"><Input type="time" required value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} /></FormField>
              <FormField label="bis"><Input type="time" required value={f.ende} onChange={(e) => setF({ ...f, ende: e.target.value })} /></FormField>
            </div>
            <p className="text-xs text-muted-foreground">Ab dem Erstgespräch läuft die Laufzeit. Erst dann sieht das Mitglied Plan und Einkaufsliste in der App.</p>
            <DialogFooter><Button type="submit" disabled={laeuft || !f.datum}>{laeuft && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Übernehmen</Button></DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Buchungen({ onAnzahl }) {
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState('');
  const [wahl, setWahl] = useState(null);
  const laden = useCallback(async () => {
    setFehler('');
    try { const r = (await ernaehrungAPI.coachings()).data; setD(r); onAnzahl?.(r.offen.length); }
    catch (e) { setFehler(fehlertext(e, 'Buchungen konnten nicht geladen werden')); setD({ offen: [], laufend: [] }); }
  }, [onAnzahl]);
  useEffect(() => { laden(); }, [laden]);
  if (d === null) return <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>;
  return (
    <div className="space-y-4">
      {fehler && <p className="text-sm text-destructive" role="alert">{fehler}</p>}
      <section aria-labelledby="offen-titel" className="space-y-2">
        <h2 id="offen-titel" className="text-sm font-semibold">Offen — wer übernimmt? ({d.offen.length})</h2>
        {d.offen.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine offenen Buchungen. Neue entstehen, wenn eine Bestellung mit einem Ernährungscoaching-Produkt im Shop bestätigt wird.</p>
        ) : (
          <ul className="divide-y rounded-md border border-amber-200 bg-amber-500/5">
            {d.offen.map((c) => (
              <li key={c.id} className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{c.member_name || 'Ohne Namen'}</p>
                  <p className="text-xs text-muted-foreground">{c.produkt} · {c.wochen} Wochen · gebucht {datum(c.erstellt_am)}</p>
                </div>
                <Button size="sm" className="self-start sm:self-auto" onClick={() => setWahl(c)}>Übernehmen</Button>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="laufend-titel" className="space-y-2">
        <h2 id="laufend-titel" className="text-sm font-semibold">Laufend ({d.laufend.length})</h2>
        {d.laufend.length === 0 ? <p className="text-sm text-muted-foreground">Kein laufendes Coaching.</p> : (
          <ul className="divide-y rounded-md border">
            {d.laufend.map((c) => (
              <li key={c.id}>
                <Link to={`/ernaehrung/${c.member_id}`} className="flex items-center gap-3 px-3 py-2.5 hover:bg-accent/40 focus:bg-accent/40 focus:outline-none">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{c.member_name || 'Ohne Namen'}</p>
                    <p className="text-xs text-muted-foreground truncate">Coach {c.coach_name || '–'} · bis {datum(c.ende, { day: '2-digit', month: '2-digit', year: '2-digit' })}{c.quelle === 'manuell' ? ' · ohne Kauf freigeschaltet' : ''}</p>
                  </div>
                  {c.laeuft_aus && <Badge variant="outline" className="shrink-0 bg-amber-500/10 text-amber-700 border-amber-200" title="Endet in den nächsten 7 Tagen — Verlängerung ansprechen">läuft aus</Badge>}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
      <UebernehmenDialog buchung={wahl} onClose={() => setWahl(null)} onFertig={laden} />
    </div>
  );
}

export const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
export const MAKRO_KACHELN = [['kcal', 'kcal', ''], ['eiweiss', 'Eiweiß', ' g'], ['kh', 'KH', ' g'], ['fett', 'Fett', ' g']];

/* Durchschnitt je Tag in vier Kacheln; Anteile = Energieanteil der Makros. */
export function MakroKacheln({ n, klein }) {
  if (!n) return <p className="text-xs text-muted-foreground">Noch keine Rezepte eingetragen.</p>;
  return (
    <div className="grid grid-cols-4 gap-1.5">
      {MAKRO_KACHELN.map(([k, label, einheit]) => (
        <div key={k} className={`rounded-md text-center ${k === 'kcal' ? 'bg-primary/10' : 'bg-muted'} ${klein ? 'py-1.5' : 'py-2'}`}>
          {k !== 'kcal' && n.anteile && <span className="block text-[10px] text-muted-foreground tabular-nums">{n.anteile[k]} %</span>}
          <span className={`block font-semibold tabular-nums ${klein ? 'text-sm' : 'text-base'}`}>{Math.round(n[k])}{einheit}</span>
          <span className="block text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
        </div>
      ))}
    </div>
  );
}

function ZuweisenDialog({ vorlage, onClose }) {
  const navigate = useNavigate();
  const [mitglied, setMitglied] = useState(null);
  const [start, setStart] = useState('');
  const [tage, setTage] = useState('7');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState('');
  useEffect(() => {
    if (!vorlage) return;
    const d = new Date();
    setMitglied(null); setFehler(''); setTage('7');
    setStart(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  }, [vorlage]);
  const zuweisen = async (e) => {
    e.preventDefault();
    setLaeuft(true); setFehler('');
    try {
      const { data } = await ernaehrungAPI.planErzeugen(mitglied.id, start, Number(tage), vorlage.id);
      toast.success(`Plan für ${mitglied.name} nach „${vorlage.name}“ erstellt${data.ersetzt?.length ? ` — ${data.ersetzt.length} Mahlzeiten passend ersetzt` : ''}`);
      onClose();
      navigate(`/ernaehrung/${mitglied.id}`);
    } catch (err) { setFehler(fehlertext(err)); } finally { setLaeuft(false); }
  };
  return (
    <Dialog open={!!vorlage} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{vorlage ? `„${vorlage.name}“ zuweisen` : ''}</DialogTitle></DialogHeader>
        <form onSubmit={zuweisen} className="space-y-3">
          {mitglied ? (
            <div className="flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
              <span className="font-medium truncate">{mitglied.name}</span>
              <Button type="button" size="sm" variant="ghost" onClick={() => setMitglied(null)}>Ändern</Button>
            </div>
          ) : <MitgliedSuche onWahl={setMitglied} />}
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Start"><Input type="date" required value={start} onChange={(e) => setStart(e.target.value)} /></FormField>
            <FormField label="Dauer"><NativeSelect value={tage} onChange={setTage} options={[3, 5, 7, 10, 14].map((n) => ({ value: String(n), label: `${n} Tage` }))} /></FormField>
          </div>
          <p className="text-xs text-muted-foreground">Jedes Rezept wird auf das Tagesziel des Mitglieds skaliert. Was nicht zu seinen Ausschlüssen passt, ersetzt der Plan und zeigt es an. Ein Plan mit demselben Start wird ersetzt.</p>
          {fehler && (
            <div className="text-sm text-destructive space-y-1" role="alert">
              <p>{fehler}</p>
              {mitglied && fehler.includes('fehlt') && <Link className="underline" to={`/ernaehrung/${mitglied.id}`}>Angaben von {mitglied.name} ausfüllen</Link>}
            </div>
          )}
          <DialogFooter><Button type="submit" disabled={laeuft || !mitglied || !start}>{laeuft && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Plan erstellen</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VorlageKarte({ v, onZuweisen, onDuplizieren, onLoeschen }) {
  const navigate = useNavigate();
  return (
    <Card className="w-full flex flex-col">
      <CardContent className="p-4 flex flex-col gap-3 flex-1">
        <div className="flex items-start gap-2">
          <h3 className="font-semibold leading-snug break-words flex-1 min-w-0">{v.name}</h3>
          <div className="flex shrink-0 -mr-2 -mt-1">
            <Button size="icon" variant="ghost" aria-label={`${v.name} bearbeiten`} onClick={() => navigate(`/ernaehrung/vorlagen/${v.id}`)}><Pencil className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" aria-label={`${v.name} duplizieren`} onClick={() => onDuplizieren(v)}><Copy className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" aria-label={`${v.name} löschen`} onClick={() => onLoeschen(v)}><Trash2 className="w-4 h-4" /></Button>
          </div>
        </div>
        {v.beschreibung && <p className="text-sm text-muted-foreground line-clamp-2 -mt-1">{v.beschreibung}</p>}
        <div className="flex flex-wrap gap-1">
          {v.passt_zu.filter((a) => ['vegetarisch', 'vegan', 'laktosefrei', 'glutenfrei'].includes(a)).map((a) => <Badge key={a} variant="outline" className="font-normal bg-emerald-500/10 text-emerald-700 border-emerald-200">{AUSSCHLUSS_NAME[a]}</Badge>)}
          {v.tags.map((t) => <Badge key={t} variant="secondary" className="font-normal">{t}</Badge>)}
          <Badge variant="outline" className="font-normal ml-auto">{v.typ === 'tag' ? 'jeden Tag gleich' : 'Woche'}</Badge>
        </div>
        {v.typ === 'woche' ? (
          <div className="grid grid-cols-7 gap-1 text-center" aria-label="Mahlzeiten je Wochentag">
            {WOCHENTAGE.map((w, i) => (
              <div key={w} className={`rounded-md border py-1 ${v.tage_info[String(i)]?.anzahl ? '' : 'opacity-50'}`}>
                <span className="block text-[10px] text-muted-foreground">{w}</span>
                <span className="block text-sm font-medium tabular-nums">{v.tage_info[String(i)]?.anzahl || 0}</span>
              </div>
            ))}
          </div>
        ) : <p className="text-sm">{v.tage_info['0']?.anzahl || 0} Mahlzeiten, jeden Tag dieselben</p>}
        <div>
          <p className="text-[11px] text-muted-foreground mb-1">Ø pro Tag bei Grundmengen — beim Zuweisen aufs Tagesziel skaliert</p>
          <MakroKacheln n={v.schnitt} klein />
          {v.schnitt?.preis && <p className="text-xs mt-1"><Preis p={v.schnitt.preis} davor="Lebensmittel: Ø " danach=" am Tag" /></p>}
        </div>
        {(v.leer > 0 || v.inaktiv.length > 0) && (
          <p className="text-xs text-amber-700 flex items-start gap-1"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
            {[v.leer > 0 && `${v.leer} ${v.leer === 1 ? 'Stelle' : 'Stellen'} leer`, v.inaktiv.length > 0 && `${v.inaktiv.length} Rezept${v.inaktiv.length === 1 ? '' : 'e'} abgeschaltet`].filter(Boolean).join(' · ')} — füllt der Plan beim Zuweisen selbst
          </p>
        )}
        <Button className="mt-auto w-full" onClick={() => onZuweisen(v)}><UserPlus className="w-4 h-4 mr-1" />Zuweisen</Button>
      </CardContent>
    </Card>
  );
}

function Vorlagen() {
  const navigate = useNavigate();
  const [daten, setDaten] = useState(null);
  const [fehler, setFehler] = useState('');
  const [filter, setFilter] = useState({ q: '', passt: '', typ: '', sort: 'neu' });
  const [zuweisen, setZuweisen] = useState(null);
  const laden = useCallback(async () => {
    setFehler('');
    try { setDaten((await ernaehrungAPI.vorlagen()).data.vorlagen); }
    catch (e) { setFehler(fehlertext(e, 'Vorlagen konnten nicht geladen werden')); setDaten([]); }
  }, []);
  useEffect(() => { laden(); }, [laden]);
  const gefiltert = useMemo(() => {
    const q = filter.q.trim().toLowerCase();
    const liste = (daten || []).filter((v) => (
      (!q || v.name.toLowerCase().includes(q) || (v.beschreibung || '').toLowerCase().includes(q) || v.tags.some((t) => t.toLowerCase().includes(q)))
      && (!filter.passt || v.passt_zu.includes(filter.passt))
      && (!filter.typ || v.typ === filter.typ)
    ));
    if (filter.sort === 'name') return [...liste].sort((a, b) => a.name.localeCompare(b.name, 'de'));
    if (filter.sort === 'kcal') return [...liste].sort((a, b) => (a.schnitt?.kcal || 0) - (b.schnitt?.kcal || 0));
    return liste;
  }, [daten, filter]);
  const duplizieren = async (v) => {
    try { const { data } = await ernaehrungAPI.vorlageDuplizieren(v.id); toast.success('Kopie angelegt'); navigate(`/ernaehrung/vorlagen/${data.id}`); }
    catch (e) { toast.error(fehlertext(e)); }
  };
  const loeschen = async (v) => {
    if (!window.confirm(`Vorlage „${v.name}“ löschen? Zugewiesene Pläne bleiben, wie sie sind.`)) return;
    try { await ernaehrungAPI.vorlageLoeschen(v.id); toast.success('Gelöscht'); laden(); }
    catch (e) { toast.error(fehlertext(e)); }
  };
  if (daten === null) return <div className="grid sm:grid-cols-2 gap-3">{[0, 1].map((i) => <Skeleton key={i} className="h-72 w-full" />)}</div>;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-[auto_1fr_repeat(3,auto)] gap-2 items-end">
        <Button className="col-span-2 lg:col-span-1" onClick={() => navigate('/ernaehrung/vorlagen/neu')}><Plus className="w-4 h-4 mr-1" />Neue Vorlage</Button>
        <div className="relative col-span-2 lg:col-span-1">
          <label htmlFor="vorlagen-suche" className="sr-only">Vorlagen suchen</label>
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" aria-hidden="true" />
          <Input id="vorlagen-suche" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} placeholder="Vorlagen suchen …" className="pl-8" />
        </div>
        <NativeSelect aria-label="Ernährungsform" value={filter.passt} onChange={(v) => setFilter({ ...filter, passt: v })} options={[{ value: '', label: 'jede Ernährung' }, ...Object.entries(AUSSCHLUSS_NAME).map(([value, label]) => ({ value, label }))]} />
        <NativeSelect aria-label="Vorlagentyp" value={filter.typ} onChange={(v) => setFilter({ ...filter, typ: v })} options={[{ value: '', label: 'alle Typen' }, { value: 'woche', label: 'Woche' }, { value: 'tag', label: 'jeden Tag gleich' }]} />
        <NativeSelect aria-label="Sortieren" value={filter.sort} onChange={(v) => setFilter({ ...filter, sort: v })} options={[{ value: 'neu', label: 'zuletzt geändert' }, { value: 'name', label: 'Name' }, { value: 'kcal', label: 'kcal' }]} />
      </div>
      {fehler && <p className="text-sm text-destructive" role="alert">{fehler}</p>}
      {daten.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground space-y-2">
          <p>Noch keine Vorlagen. Eine Vorlage legt fest, welches Rezept es an welchem Wochentag zu welcher Mahlzeit gibt — beim Zuweisen rechnet der Plan die Mengen fürs jeweilige Mitglied.</p>
          <p>„Neue Vorlage“ → „Automatisch füllen“ ist der schnellste Start. Oder am Plan eines Mitglieds „Als Vorlage speichern“.</p>
        </CardContent></Card>
      ) : (
        <>
          <p className="text-sm text-muted-foreground" aria-live="polite">{gefiltert.length} {gefiltert.length === 1 ? 'Vorlage' : 'Vorlagen'} gefunden</p>
          <ul className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {gefiltert.map((v) => <li key={v.id} className="flex"><VorlageKarte v={v} onZuweisen={setZuweisen} onDuplizieren={duplizieren} onLoeschen={loeschen} /></li>)}
          </ul>
        </>
      )}
      <ZuweisenDialog vorlage={zuweisen} onClose={() => setZuweisen(null)} />
    </div>
  );
}

// Formulare und Kochbücher laden ihre Helfer aus dieser Datei — darum dynamisch, sonst kreisen die Importe
const FormulareReiter = lazy(() => import('@/pages/ErnaehrungFormulare'));
const KochbuecherReiter = lazy(() => import('@/pages/ErnaehrungKochbuecher'));
const AutomatikReiter = lazy(() => import('@/pages/ErnaehrungAutomatik'));

export default function ErnaehrungPage() {
  const [offene, setOffene] = useState(0);
  const [ungelesen, setUngelesen] = useState(0);
  useEffect(() => { ernaehrungAPI.formulareEingang().then((r) => setUngelesen(r.data.ungelesen)).catch(() => {}); }, []);
  const [params, setParams] = useSearchParams();
  const reiter = ['mitglieder', 'buchungen', 'formulare', 'vorlagen', 'rezepte', 'kochbuecher', 'automatik', 'einstellungen'].includes(params.get('reiter')) ? params.get('reiter') : 'mitglieder';
  useEffect(() => { ernaehrungAPI.coachings().then((r) => setOffene(r.data.offen.length)).catch(() => {}); }, []);
  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Salad className="w-6 h-6 shrink-0 text-primary" aria-hidden="true" />
        <div>
          <h1 className="text-xl font-semibold">Ernährung</h1>
          <p className="text-sm text-muted-foreground">Ernährungscoaching zum Zubuchen: Wochenpläne aus P1-Rezepten — das Mitglied sieht sie bei laufendem Coaching in der App.</p>
        </div>
      </div>
      <Tabs value={reiter} onValueChange={(r) => setParams(r === 'mitglieder' ? {} : { reiter: r }, { replace: true })}>
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="mitglieder">Mitglieder</TabsTrigger>
          <TabsTrigger value="buchungen">Buchungen{offene ? <span className="ml-1.5 rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-white" aria-label={`${offene} offen`}>{offene}</span> : null}</TabsTrigger>
          <TabsTrigger value="formulare">Check-ins{ungelesen ? <span className="ml-1.5 rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground" aria-label={`${ungelesen} neue Antworten`}>{ungelesen}</span> : null}</TabsTrigger>
          <TabsTrigger value="vorlagen">Vorlagen</TabsTrigger>
          <TabsTrigger value="rezepte">Rezepte</TabsTrigger>
          <TabsTrigger value="kochbuecher">Kochbücher</TabsTrigger>
          <TabsTrigger value="automatik">Automatik</TabsTrigger>
          <TabsTrigger value="einstellungen">Einstellungen</TabsTrigger>
        </TabsList>
        <TabsContent value="mitglieder" className="mt-3"><Mitglieder /></TabsContent>
        <TabsContent value="buchungen" className="mt-3"><Buchungen onAnzahl={setOffene} /></TabsContent>
        <TabsContent value="formulare" className="mt-3"><Suspense fallback={<Skeleton className="h-64 w-full" />}><FormulareReiter onUngelesen={setUngelesen} /></Suspense></TabsContent>
        <TabsContent value="vorlagen" className="mt-3"><Vorlagen /></TabsContent>
        <TabsContent value="rezepte" className="mt-3"><Rezepte /></TabsContent>
        {/* E3 (15.10.2026): Kochbücher — Rezeptbibliothek · Abschnitte · Details und Sichtbarkeit */}
        <TabsContent value="kochbuecher" className="mt-3"><Suspense fallback={<Skeleton className="h-64 w-full" />}><KochbuecherReiter /></Suspense></TabsContent>
        {/* E7 (15.10.2026): Automatik-Sequenzen — Liste von Schritten je Auslöser, Läufe je Mitglied */}
        <TabsContent value="automatik" className="mt-3"><Suspense fallback={<Skeleton className="h-64 w-full" />}><AutomatikReiter /></Suspense></TabsContent>
        {/* Einstellungen (11.10.2026): Funktionen der Kunden-App — Genuss-Planer; nur die Leitung schaltet */}
        {/* Tresor (E8, 15.10.2026): Einträge für ALLE Coaching-Kunden; je Mitglied auf Ernährung › Mitglied */}
        <TabsContent value="einstellungen" className="mt-3"><FunktionenSchalter /><AboProduktEinstellung /><EinwilligungFreigabe /><KursTipps /><RisikoEinstellung /><WearableSchwellen /><KommentarVorlagen /><MedienFeedbackEinstellung /><div className="mt-4"><ErnaehrungTresor leadId={null} titel="Tresor (für alle)" /></div></TabsContent>
      </Tabs>
    </div>
  );
}
